import React, { useEffect, useState } from "react"
import { injectIntl } from "react-intl"
import * as Yup from "yup"
import { makeStyles } from "@material-ui/core/styles"
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"
import RefreshIcon from "@material-ui/icons/Refresh"
import CreateIcon from "@material-ui/icons/Create"
import { Box, Card, Grid, Typography } from "@material-ui/core"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"
import {
  TextInput,
  FormikForm,
  FormikFormContainer,
  PhoneNumberInput,
} from "@ridecell/web-components-material"
import { Ridecell } from "@ridecell/web-sdk"
import useGlobalContext from "../../../utils/customHooks/useGlobalContext"
import LoadingButton from "../../LoadingButton"
import useCustomer from "../../../utils/customHooks/useCustomer"
import messages from "./messages"

const validationSchema = (formatMessage) =>
  Yup.object().shape({
    phone_number: Yup.string()
      .required(formatMessage(messages.phoneNumberRequired))
      .nullable(),
  })

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  card: {
    margin: "24px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    boxShadow:
      "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)",
    borderRadius: "4px",
  },
  subTitle: {
    fontSize: "16px",
    lineHeight: "28px",
    textAlign: "center",
  },
  littleText: {
    fontSize: "12px",
    lineHeight: "20px",
    textAlign: "center",
  },
  phoneText: {
    fontSize: "20px",
    fontWeight: 500,
  },
  backIcon: {
    cursor: "pointer",
  },
})

const VerifyCustomer = ({
  handleNext,
  intl: { formatMessage },
  createCustomer,
  defaultSteps,
  history,
  ...props
}) => {
  const classes = useStyles()
  const [editPhone, setEditPhone] = useState(false)
  const {
    state: { customer, loading, showBackdrop, tenantSettings },
    setLoading,
    setCurrentUser,
    toggleBackdrop,
    setError,
  } = useGlobalContext()
  const [verificationText, setVerificationText] = useState("")
  const verificationMode = tenantSettings.customerVerificationModeString
  const emailVerification = verificationMode === "email"
  const { fetchCustomer } = useCustomer()

  const submitHandler = async () => {
    setLoading(true)
    try {
      const data = { verification_code: verificationText }
      await Ridecell.verifyPhoneNumber({ customerId: customer?.id, data })
      if (defaultSteps) {
        await fetchCustomer()
        history.push({ pathname: `/`, state: { openProfilePanel: true } })
      } else handleNext()
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  const reSendOtp = async () => {
    const data = {
      ...(!emailVerification && { phone_number: customer?.phone_number }),
      ...(emailVerification && { email: customer?.email }),
    }
    toggleBackdrop(true)
    await Ridecell.resendVerificationCode({
      customerId: customer?.id,
      data,
    }).catch((e) => setError(e))
    toggleBackdrop(false)
  }

  useEffect(() => {
    ;(() => {
      const { currentStep } = props
      if (
        (!customer?.phone_number_verified && currentStep === 1) ||
        defaultSteps
      )
        reSendOtp()
    })()
  }, [])

  return (
    <Grid item xs={12} md={12} lg={6}>
      <Card className={classes.card}>
        {!editPhone && (
          <React.Fragment>
            <Box display="flex" sx={{ mb: "16px" }} justifyContent="center">
              <Typography className={classes.subTitle}>
                {formatMessage(messages.verificationMessage, {
                  communication: emailVerification ? "email" : "text",
                })}
              </Typography>
            </Box>
            <Box display="flex" sx={{ mb: "16px" }} justifyContent="center">
              <Typography
                variant={emailVerification ? "h7" : "h6"}
                sx={{ marginRight: "8px" }}
              >
                {emailVerification ? customer?.email : customer?.phone_number}
              </Typography>
              {!emailVerification && (
                <Box sx={{ ml: "8px" }} alignSelf={"center"}>
                  <CreateIcon
                    style={{ cursor: "pointer" }}
                    onClick={() => setEditPhone(true)}
                    color="secondary"
                  />
                </Box>
              )}
            </Box>
            <Box display="flex" sx={{ mb: "16px" }}>
              <TextInput
                color="primary"
                fullWidth
                placeholder={formatMessage(messages.otp)}
                name="otp"
                size="medium"
                type="text"
                onChange={(e) => setVerificationText(e.currentTarget.value)}
                variant="standard"
                InputLabelProps={{
                  shrink: false,
                }}
              />
            </Box>
          </React.Fragment>
        )}
        {editPhone && (
          <React.Fragment>
            <FormikFormContainer
              initialValues={{
                phone_number: customer?.phone_number || "",
              }}
              onSubmit={async (values) => {
                setLoading(true)
                try {
                  const resp = await Ridecell.updateCustomer({
                    customerId: customer.id,
                    data: values,
                  })
                  setCurrentUser(resp)
                } catch (e) {
                  setError(e)
                } finally {
                  setEditPhone(false)
                  setLoading(false)
                }
              }}
              validationSchema={validationSchema(formatMessage)}
            >
              {({ isValid, values, setFieldValue }) => (
                <FormikForm>
                  <ArrowBackIcon
                    className={classes.backIcon}
                    onClick={() => setEditPhone(false)}
                  />
                  <Box display="flex" sx={{ mb: "16px", mt: "16px" }}>
                    <PhoneNumberInput
                      id="phone-input"
                      color="primary"
                      fullWidth
                      label={formatMessage(messages.phoneNumber)}
                      name="phone_number"
                      size="medium"
                      type="text"
                      variant="standard"
                      value={values.phone_number}
                      onChange={(phone) => {
                        setFieldValue("phone_number", phone)
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Box>
                  <LoadingButton
                    disabled={!isValid}
                    text={formatMessage(messages.next)}
                    variant="contained"
                    loading={loading}
                    fullWidth
                    type="submit"
                    props={{
                      fullWidth: true,
                      endIcon: <ArrowForwardIcon />,
                      size: "large",
                    }}
                  />
                </FormikForm>
              )}
            </FormikFormContainer>
          </React.Fragment>
        )}
        {!editPhone && (
          <Box display="flex" sx={{ mb: "16px" }}>
            <LoadingButton
              type="submit"
              text={formatMessage(messages.nextCapital)}
              loading={loading}
              fullWidth
              onClick={submitHandler}
              props={{
                fullWidth: true,
                endIcon: !editPhone && <ArrowForwardIcon />,
                size: "large",
                variant: "contained",
              }}
            />
          </Box>
        )}
        {!editPhone && (
          <React.Fragment>
            <Box display="flex" sx={{ mb: "16px" }} justifyContent="center">
              <Typography className={classes.littleText}>
                {formatMessage(messages.didntReceiveCode)}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="center" alignSelf={"center"}>
              <LoadingButton
                type="submit"
                text={formatMessage(messages.resendCode)}
                loading={showBackdrop}
                fullWidth
                onClick={reSendOtp}
                props={{
                  startIcon: <RefreshIcon />,
                  size: "small",
                  color: "secondary",
                  variant: "contained",
                }}
              />
            </Box>
          </React.Fragment>
        )}
      </Card>
    </Grid>
  )
}

export default injectIntl(VerifyCustomer)
